/**
 * Hook to synchronize chat messages between RTK Query and Redux store.
 * Fetches messages via API and dispatches to Redux as single source of truth.
 * Skips API fetch for conversations that were just created locally via WebSocket.
 */

import { useGetAgentMessagesQuery } from '@saaga/shared/store/api.store'
import {
  clearLocallyCreatedFlag,
  incrementPage,
  resetPagination,
  syncMessagesFromApi,
} from '@saaga/shared/store/slices/chat.slice'
import {
  selectIsConversationLocallyCreated,
  selectPageForConversation,
} from '@saaga/shared/store/slices/chat-selectors.store'
import type { AppDispatch } from '@saaga/shared/store/store'
import { useAppDispatch, useAppSelector } from '@saaga/shared/store/store'
import { useCallback, useEffect, useRef } from 'react'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'
import { transformMessageListToEntityList } from '@/utils/message-entity-transformer.util'

export const CHAT_MESSAGES_PAGE_SIZE = 200
/**
 * Canonical include_notes used by every messages-list read path (initial
 * load, load-more, scroll-to-pinned). Mismatched values produce different
 * RTK Query cache keys that both sync back to Redux and make Virtuoso's
 * item indexes oscillate.
 */
export const CHAT_MESSAGES_INCLUDE_NOTES = true

interface UseChatMessageSyncParams {
  conversationId: string | null
  userLookup: UserLookupMap
}

interface UseChatMessageSyncReturn {
  isLoading: boolean
  isFetching: boolean
  loadMoreMessages: () => void
}

const useConversationNavigationCleanup = (
  conversationId: string | null,
  dispatch: AppDispatch
): void => {
  const prevConversationIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      if (prevConversationIdRef.current) {
        dispatch(clearLocallyCreatedFlag({ conversationId: prevConversationIdRef.current }))
      }
      if (conversationId) {
        dispatch(resetPagination({ conversationId }))
      }
      prevConversationIdRef.current = conversationId
    }
  }, [conversationId, dispatch])
}

export const useChatMessageSync = ({
  conversationId,
  userLookup,
}: UseChatMessageSyncParams): UseChatMessageSyncReturn => {
  const dispatch = useAppDispatch()
  const page = useAppSelector((state) =>
    conversationId ? selectPageForConversation(state, conversationId) : 1
  )
  const isLocallyCreated = useAppSelector((state) =>
    conversationId ? selectIsConversationLocallyCreated(state, conversationId) : false
  )

  const shouldSkipFetch = !conversationId || isLocallyCreated

  const { data, isLoading, isFetching } = useGetAgentMessagesQuery(
    {
      conversationId: conversationId ?? '',
      params: {
        page,
        page_size: CHAT_MESSAGES_PAGE_SIZE,
        include_notes: CHAT_MESSAGES_INCLUDE_NOTES,
      },
    },
    { skip: shouldSkipFetch, refetchOnMountOrArgChange: true }
  )

  useConversationNavigationCleanup(conversationId, dispatch)

  useEffect(() => {
    if (shouldSkipFetch) return
    if (!data?.items || !conversationId) return

    const entityList = transformMessageListToEntityList(data.items, userLookup)

    dispatch(
      syncMessagesFromApi({
        conversationId,
        messageList: entityList,
        hasMore: data.has_more,
        isFirstPage: page === 1,
      })
    )
  }, [data, conversationId, page, userLookup, dispatch, shouldSkipFetch])

  const loadMoreMessages = useCallback((): void => {
    if (!conversationId || isFetching) return
    dispatch(incrementPage({ conversationId }))
  }, [conversationId, isFetching, dispatch])

  return {
    isLoading,
    isFetching,
    loadMoreMessages,
  }
}
